import type { SuperDocConfig } from '@superdoc/react';
import type { SuperDocFontFamily } from 'superdoc';
import type { FontOption } from '@/components/bundle/bundleTypes';

type SuperDocFontsConfig = NonNullable<SuperDocConfig['fonts']>;
type SuperDocUiConfig = Extract<SuperDocConfig['ui'], { toolbar?: unknown }>;
type SuperDocToolbarConfig = Extract<NonNullable<SuperDocUiConfig['toolbar']>, { fonts?: unknown }>;
// `ui.toolbar.fonts` takes dropdown rows only; the legacy `modules.toolbar.fonts`
// spelling also accepted loadable families and is removed in v3.
type DraftSuperDocToolbarFont = NonNullable<SuperDocToolbarConfig['fonts']>[number];

export type DraftSuperDocFontConfig = {
  fonts?: SuperDocFontsConfig;
  toolbarFonts?: DraftSuperDocToolbarFont[];
};

export function buildDraftSuperDocFontConfig(fontOptions: FontOption[]): DraftSuperDocFontConfig {
  const families: SuperDocFontFamily[] = [];
  const toolbarFonts: DraftSuperDocToolbarFont[] = [];
  const seenLabels = new Set<string>();
  const registeredFamilies = new Set<string>();

  for (const option of fontOptions) {
    const label = option.label.trim();
    const cssFamilies = splitCssFontFamily(option.fontFamily);
    const physicalFamily = cssFamilies[0];
    if (!label || !physicalFamily) continue;

    const labelKey = label.toLowerCase();
    if (seenLabels.has(labelKey)) continue;
    seenLabels.add(labelKey);

    const faces = [
      fontFace(option.files.regular, 400, 'normal'),
      fontFace(option.files.bold, 700, 'normal'),
      fontFace(option.files.italic, 400, 'italic'),
      fontFace(option.files.bold_italic, 700, 'italic'),
    ].filter((face): face is NonNullable<typeof face> => Boolean(face));

    if (faces.length === 0) continue;

    // v2 dropped the logical-to-physical `fonts.map`, so register the faces under
    // the logical Word name the document references, and again under the physical
    // family when the bundle names them differently.
    for (const family of [label, physicalFamily]) {
      const familyKey = family.toLowerCase();
      if (registeredFamilies.has(familyKey)) continue;
      registeredFamilies.add(familyKey);
      families.push({ family, faces });
    }

    toolbarFonts.push({
      key: `${label}, ${genericCssFamily(cssFamilies)}`,
      label,
      props: {
        style: {
          fontFamily: option.fontFamily,
          fontWeight: 400,
        },
      },
    });
  }

  if (families.length === 0) return {};

  return {
    fonts: { families },
    toolbarFonts,
  };
}

function fontFace(source: string | undefined, weight: number, style: string) {
  if (!source?.trim()) return null;
  return {
    source,
    style,
    weight,
  };
}

function splitCssFontFamily(fontFamily: string): string[] {
  const families: string[] = [];
  let current = '';
  let quote: '"' | "'" | null = null;

  for (const char of fontFamily) {
    if ((char === '"' || char === "'") && quote === null) {
      quote = char;
      continue;
    }
    if (char === quote) {
      quote = null;
      continue;
    }
    if (char === ',' && quote === null) {
      pushCssFamily(families, current);
      current = '';
      continue;
    }
    current += char;
  }

  pushCssFamily(families, current);
  return families;
}

function pushCssFamily(families: string[], value: string) {
  const family = value.trim();
  if (family) families.push(family);
}

function genericCssFamily(families: string[]) {
  const generic = families.find((family) => {
    const normalised = family.toLowerCase();
    return normalised === 'serif' || normalised === 'sans-serif' || normalised === 'monospace';
  });
  if (generic) return generic.toLowerCase();

  const css = families.join(',').toLowerCase();
  if (css.includes('monospace')) return 'monospace';
  if (css.includes('sans-serif')) return 'sans-serif';
  if (css.includes('serif')) return 'serif';
  return 'sans-serif';
}
